import React from "react";
import Navbar from "../Navbar";
import Footer from "../Footer";
import CreditScoringIllustration from "../../Assets/credit-scoring.png";

const CreditScoring = () => {
  return (
    <>
      <Navbar />
      <main className="credit-scoring-page" role="main">
      {/* Scoped styles for Credit Scoring page */}
      <style>{`
        .credit-scoring-page {
          background: #ffffff;
          color: #1f2937;
          min-height: 100vh;
        }

        /* Hero */
        .cs-hero {
          background: linear-gradient(135deg, #0B3B2F 0%, #065E49 50%, #118D57 100%);
          color: #ffffff;
          overflow: hidden;
        }
        .cs-hero-inner {
          max-width: 1200px;
          margin: 0 auto;
          padding: 4.5rem 1.5rem;
          display: flex;
          gap: 2rem;
          align-items: center;
          justify-content: space-between;
        }
        .cs-hero-left {
          flex: 1 1 55%;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .cs-badge {
          display: inline-flex;
          align-items: center;
          gap: .5rem;
          background: rgba(255,255,255,0.14);
          border: 1px solid rgba(255,255,255,0.3);
          color: #E7F8EF;
          padding: .35rem .75rem;
          font-weight: 700;
          border-radius: 999px;
          width: fit-content;
          backdrop-filter: blur(4px);
        }
        .cs-title {
          font-size: clamp(2rem, 4.8vw, 3.25rem);
          line-height: 1.15;
          font-weight: 800;
          margin: .25rem 0 .5rem 0;
          letter-spacing: .2px;
          text-wrap: balance;
        }
        .cs-subtitle {
          font-size: clamp(1.05rem, 2.5vw, 1.25rem);
          line-height: 1.8;
          color: #F2FFF8;
          opacity: 0.95;
          max-width: 46ch;
        }
        .cs-hero-right {
          flex: 1 1 45%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .cs-hero-illustration {
          width: 100%;
          max-width: 680px;
          height: auto;
          filter: drop-shadow(0 18px 36px rgba(0,0,0,.25));
        }

        /* Features */
        .cs-section {
          max-width: 1200px;
          margin: 0 auto;
          padding: 3.5rem 1.5rem 5rem 1.5rem;
        }
        .cs-section-header {
          margin-bottom: 1.75rem;
        }
        .cs-section-title {
          font-size: clamp(1.5rem, 3.5vw, 2rem);
          font-weight: 800;
          color: #0B3B2F;
          margin: 0 0 .5rem 0;
        }
        .cs-section-underline {
          width: 80px;
          height: 4px;
          background: linear-gradient(90deg, #118D57 0%, #5BE49B 100%);
          border-radius: 2px;
        }
        .cs-features-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.25rem;
          margin-top: 1rem;
        }
        .cs-card {
          background: #ffffff;
          border: 1.5px solid rgba(17, 141, 87, 0.18);
          border-radius: 16px;
          padding: 1.75rem;
          box-shadow: 0 8px 30px rgba(6, 94, 73, 0.08);
          transition: transform .25s ease, box-shadow .25s ease, border-color .25s ease;
          position: relative;
          overflow: hidden;
        }
        .cs-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, #118D57 0%, #5BE49B 100%);
          transform: scaleX(0);
          transform-origin: left;
          transition: transform .25s ease;
        }
        .cs-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 16px 44px rgba(6, 94, 73, 0.16);
          border-color: rgba(17, 141, 87, 0.35);
        }
        .cs-card:hover::before {
          transform: scaleX(1);
        }
        .cs-card-number {
          width: 48px;
          height: 48px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 12px;
          background: rgba(91, 228, 155, 0.18);
          color: #0B604A;
          font-weight: 800;
          font-size: 1.1rem;
          margin-bottom: 1rem;
          transition: transform .25s ease, background .25s ease;
        }
        .cs-card:hover .cs-card-number {
          transform: scale(1.1) rotate(-5deg);
          background: rgba(17, 141, 87, 0.2);
        }
        .cs-card-title {
          margin: 0 0 .65rem 0;
          font-size: 1.1rem;
          font-weight: 800;
          color: #063C30;
        }
        .cs-card-description {
          line-height: 1.75;
          color: #374151;
          font-size: 0.95rem;
          margin: 0;
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .cs-hero-inner {
            padding: 3.25rem 1.25rem;
          }
          .cs-features-grid {
            grid-template-columns: 1fr 1fr;
          }
        }
        @media (max-width: 768px) {
          .cs-hero-inner {
            flex-direction: column-reverse;
            text-align: left;
            gap: 1.5rem;
          }
          .cs-hero-right {
            width: 100%;
          }
          .cs-hero-illustration {
            max-width: 560px;
          }
          .cs-features-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      {/* Hero */}
      <section className="cs-hero">
        <div className="cs-hero-inner">
          <div className="cs-hero-left">
            <span className="cs-badge" aria-label="AI-Powered">AI-Powered Solution</span>
            <h1 className="cs-title">Alternative Credit Scoring</h1>
            <p className="cs-subtitle">
              Transform lending decisions with our AI-powered credit scoring engine. 
              Upload mobile money statements or bank statements, and get comprehensive 
              financial analysis, credit scoring, and risk assessment—all in less than 60 seconds. 
              Our system generates personalized creditworthiness scores that help lenders make 
              confident, data-driven decisions for every customer.
            </p>
          </div>

          <div className="cs-hero-right">
            <img
              src={CreditScoringIllustration}
              className="cs-hero-illustration"
              alt="Credit scoring analytics dashboard"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="cs-section" aria-label="Credit Scoring Features">
        <header className="cs-section-header">
          <h2 className="cs-section-title">Lightning-Fast Credit Assessment</h2>
          <div className="cs-section-underline" />
        </header>

        <div className="cs-features-grid">
          <article className="cs-card" aria-labelledby="cs-analysis-title">
            <div className="cs-card-number">1</div>
            <h3 id="cs-analysis-title" className="cs-card-title">Financial Analysis</h3>
            <p className="cs-card-description">
              Our AI instantly analyzes transaction patterns, income streams, spending behavior, 
              and cash flow trends from uploaded statements to build a complete financial profile.
            </p>
          </article>

          <article className="cs-card" aria-labelledby="cs-scoring-title">
            <div className="cs-card-number">2</div>
            <h3 id="cs-scoring-title" className="cs-card-title">Credit Scoring</h3>
            <p className="cs-card-description">
              Generate personalized credit scores based on alternative data including mobile money 
              transactions, behavioral patterns, and payment history—reaching the underbanked.
            </p>
          </article>

          <article className="cs-card" aria-labelledby="cs-risk-title">
            <div className="cs-card-number">3</div>
            <h3 id="cs-risk-title" className="cs-card-title">Risk Assessment</h3>
            <p className="cs-card-description">
              Get comprehensive risk profiles with probability of default calculations, 
              recommended loan amounts, and actionable insights—all in under 60 seconds.
            </p>
          </article>
        </div>
      </section>
    </main>
    <Footer />
    </>
  );
};

export default CreditScoring;